<template>
  <UserListCard
    v-if="canView"
    :title="$t('label.dashboardPendingArticles')"
    :widget="widget"
    :view-all-to="{ name: 'Article' }"
    @retry="loadArticles()"
    @page="loadArticles($event)"
  >
    <b-table
      :items="widget.articles"
      :fields="fields"
      primary-key="uuid"
      responsive
      show-empty
    >
      <template v-if="permission === 2" #cell(actions)="{ item }">
        <b-button
          variant="success"
          size="sm"
          class="mr-1"
          :disabled="actionPending || item.status !== 1"
          :title="$t('label.publish')"
          :aria-label="$t('label.publish')"
          @click="confirmArticleStatus(item, 2)"
        >
          <i class="mdi mdi-check" aria-hidden="true"></i>
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          :disabled="actionPending || item.status !== 1"
          :title="$t('label.dashboardReject')"
          :aria-label="$t('label.dashboardReject')"
          @click="confirmArticleStatus(item, 3)"
        >
          <i class="mdi mdi-close" aria-hidden="true"></i>
        </b-button>
      </template>
    </b-table>
  </UserListCard>
</template>
<script>
import moment from "moment";
import UserListCard from "./UserListCard.vue";
import { dashboardService } from "@/services/api/dashboard.service";
import { articleService } from "@/services/api/article.service";
import { alertHelper, sharedHelper, sessionHelper } from "@/utils";

export default {
  name: "DashboardPendingArticles",
  components: { UserListCard },
  props: {
    permission: { type: Number, default: 0 },
  },
  data() {
    return {
      widget: {
        articles: [],
        total: 0,
        page: 1,
        pageSize: 15,
        loading: true,
        error: false,
      },
      requestId: 0,
      actionPending: false,
    };
  },
  computed: {
    canView() {
      return this.permission >= 1;
    },
    fields() {
      const fields = [
        { key: "title", label: this.$t("label.title") },
        { key: "author", label: this.$t("label.dashboardArticleAuthor") },
        { key: "createdBy", label: this.$t("label.dashboardArticleCreatedBy") },
        { key: "category", label: this.$t("label.dashboardArticleCategory") },
        { key: "type", label: this.$t("label.type") },
        { key: "createdAt", label: this.$t("label.createdAt") },
      ];
      if (this.permission === 2)
        fields.push({ key: "actions", label: this.$t("label.action") });
      return fields;
    },
  },
  watch: {
    canView() {
      this.loadArticles(1);
    },
  },
  created() {
    this.loadArticles();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    async confirmArticleStatus(article, status) {
      if (
        this.actionPending ||
        ![2, 3].includes(status) ||
        !article.uuid ||
        article.status !== 1
      )
        return;
      if (
        this.permission !== 2 ||
        sessionHelper.getPermissionLevel("article_approval") !== 2
      )
        return;
      const action = status === 2 ? "Publish" : "Reject";
      this.actionPending = true;
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t(`label.dashboardArticle${action}Confirm`, [article.title]),
          {
            title: this.$t(
              status === 2 ? "label.publish" : "label.dashboardReject",
            ),
            okTitle: this.$t(
              status === 2 ? "label.publish" : "label.dashboardReject",
            ),
            cancelTitle: this.$t("label.cancel"),
            okVariant: status === 2 ? "success" : "danger",
            centered: true,
          },
        );
        if (
          !confirmed ||
          this.permission !== 2 ||
          sessionHelper.getPermissionLevel("article_approval") !== 2
        )
          return;
        const result = await articleService.updateStatus(article.uuid, status);
        if (!result?.success)
          throw new Error(
            result?.message || this.$t("label.dashboardArticleStatusError"),
          );
        alertHelper.alertSuccess({
          message: this.$t(`label.dashboardArticle${action}Success`, [
            article.title,
          ]),
        });
        await this.loadArticles();
        if (
          !this.widget.error &&
          !this.widget.articles.length &&
          this.widget.page > 1
        ) {
          await this.loadArticles(
            Math.max(1, Math.ceil(this.widget.total / this.widget.pageSize)),
          );
        }
      } catch (error) {
        alertHelper.alertError({
          message:
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.dashboardArticleStatusError"),
          title: this.$t("message.error"),
        });
      } finally {
        this.actionPending = false;
      }
    },
    async loadArticles(page = this.widget.page) {
      const requestId = ++this.requestId;
      const widget = this.widget;
      if (!this.canView) {
        widget.articles = [];
        widget.total = 0;
        widget.page = 1;
        widget.loading = false;
        widget.error = false;
        return;
      }
      widget.loading = true;
      widget.error = false;
      try {
        const result = await dashboardService.getPendingArticles(
          page,
          widget.pageSize,
        );
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid pending article list.");
        if (requestId !== this.requestId) return;
        widget.articles = result.data.map((article) => {
          const createdAt = moment(article.created_at, moment.ISO_8601, true);
          const creator = article.created_by;
          return {
            uuid: article.uuid,
            status: article.status == null ? null : Number(article.status),
            title: article.title || "—",
            author: article.author_name || "—",
            createdBy:
              creator?.name && creator?.email
                ? `${creator.name}(${creator.email})`
                : creator?.name || creator?.email || "—",
            category: article.article_category?.name || "—",
            type: article.article_type?.name || "—",
            createdAt:
              article.created_at && createdAt.isValid()
                ? createdAt.utcOffset(480).format("YYYY-MM-DD HH:mm:ss")
                : "—",
          };
        });
        widget.total = result.pagination?.total ?? result.data.length;
        widget.page = result.pagination?.current_page || page;
        widget.pageSize = result.pagination?.per_page || widget.pageSize;
      } catch (error) {
        if (requestId === this.requestId) widget.error = true;
      } finally {
        if (requestId === this.requestId) widget.loading = false;
      }
    },
  },
};
</script>
